import { getRegistrationData } from '../data/getRegistrationData';
import type { Lang } from '../i18n/types';

export type QuizTypeKey = 'A' | 'B' | 'C' | 'D';
export type SingleQuizTypeKey = 'A' | 'B' | 'C';

export type PersonalityVariantKey =
  | 'A1' | 'A2' | 'A3'
  | 'B1' | 'B2' | 'B3'
  | 'C1' | 'C2' | 'C3'
  | 'D1' | 'D2' | 'D3';

export type FrozenHeartVariantKey =
  | 'A1' | 'A2' | 'A3'
  | 'B1' | 'B2' | 'B3'
  | 'C1' | 'C2' | 'C3';

export interface QuizScore {
  type: QuizTypeKey;
  percent: number;
  counts: Record<QuizTypeKey, number>;
}

export interface SingleQuizScore {
  type: SingleQuizTypeKey;
  percent: number;
  counts: Record<SingleQuizTypeKey, number>;
  maxCount: number;
}

export function scoreQuizAnswers(answers: Record<number, string>): QuizScore {
  const counts: Record<QuizTypeKey, number> = { A: 0, B: 0, C: 0, D: 0 };
  Object.values(answers).forEach((type) => {
    if (type === 'A' || type === 'B' || type === 'C' || type === 'D') {
      counts[type]++;
    }
  });
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0) || 1;

  let maxType: QuizTypeKey = 'A';
  let maxCount = -1;
  (Object.entries(counts) as [QuizTypeKey, number][]).forEach(([type, cnt]) => {
    if (cnt > maxCount) {
      maxCount = cnt;
      maxType = type;
    }
  });

  return {
    type: maxType,
    percent: Math.round((counts[maxType] / total) * 100),
    counts,
  };
}

/** Bốc ngẫu nhiên 1 trong 3 biến thể wording sau khi xác định nhóm A/B/C/D */
export function pickPersonalityVariant(type: QuizTypeKey): PersonalityVariantKey {
  const index = Math.floor(Math.random() * 3) + 1;
  return `${type}${index}` as PersonalityVariantKey;
}

export interface PersonalityResultPayload {
  title: string;
  desc: string;
  shareTitle: string;
  shareHook: string;
  typeKey: QuizTypeKey;
  variantKey: PersonalityVariantKey;
  percent: number;
  answers: Record<number, string>;
}

export function buildPersonalityResult(
  answers: Record<number, string>,
  lang: Lang = 'vi',
  forcedVariant?: string
): PersonalityResultPayload {
  const { PERSONALITY_RESULTS, PERSONALITY_VARIANTS } = getRegistrationData(lang);
  const { type, percent } = scoreQuizAnswers(answers);
  const result = PERSONALITY_RESULTS[type];
  let variantKey: PersonalityVariantKey;
  if (forcedVariant && forcedVariant.length === 2 && forcedVariant[0] === type) {
    variantKey = forcedVariant as PersonalityVariantKey;
  } else {
    variantKey = pickPersonalityVariant(type);
  }
  const variant = PERSONALITY_VARIANTS[variantKey];
  return {
    title: result.title,
    desc: result.desc,
    shareTitle: variant.shareTitle,
    shareHook: variant.shareHook,
    typeKey: type,
    variantKey,
    percent,
    answers,
  };
}

/** Phần trăm ngẫu nhiên theo Count Max — quiz Trái tim băng giá */
export function randomFrozenHeartPercent(maxCount: number): number {
  let min: number;
  let max: number;
  if (maxCount <= 4) {
    min = 37.5;
    max = 43.2;
  } else if (maxCount <= 6) {
    min = 58.4;
    max = 66.7;
  } else if (maxCount <= 8) {
    min = 75.2;
    max = 84.6;
  } else {
    min = 91.3;
    max = 98.9;
  }
  const value = min + Math.random() * (max - min);
  return Math.round(value * 10) / 10;
}

/** Đếm nhóm A/B/C; hòa ưu tiên A → B → C */
export function scoreSingleQuizAnswers(answers: Record<number, string>): SingleQuizScore {
  const counts: Record<SingleQuizTypeKey, number> = { A: 0, B: 0, C: 0 };
  Object.values(answers).forEach((type) => {
    if (type === 'A' || type === 'B' || type === 'C') {
      counts[type]++;
    }
  });

  let maxType: SingleQuizTypeKey = 'A';
  let maxCount = -1;
  (['A', 'B', 'C'] as const).forEach((type) => {
    if (counts[type] > maxCount) {
      maxCount = counts[type];
      maxType = type;
    }
  });

  return {
    type: maxType,
    percent: randomFrozenHeartPercent(maxCount),
    counts,
    maxCount,
  };
}

const FROZEN_HEART_TEMP_RANGES: Record<SingleQuizTypeKey, [number, number]> = {
  A: [-15, -5],
  B: [-45, -30],
  C: [-80, -65],
};

/** Bốc ngẫu nhiên 1 trong 3 biến thể wording sau khi xác định nhóm A/B/C */
export function pickFrozenHeartVariant(type: SingleQuizTypeKey): FrozenHeartVariantKey {
  const index = Math.floor(Math.random() * 3) + 1;
  return `${type}${index}` as FrozenHeartVariantKey;
}

/** Nhiệt độ ngẫu nhiên trong khoảng của từng nhóm */
export function randomFrozenHeartTemperature(type: SingleQuizTypeKey): string {
  const [min, max] = FROZEN_HEART_TEMP_RANGES[type];
  const value = min + Math.random() * (max - min);
  return `${Math.round(value * 10) / 10}°C`;
}

export interface FrozenHeartResultPayload {
  title: string;
  desc: string;
  shareTitle: string;
  shareHook: string;
  typeKey: SingleQuizTypeKey;
  variantKey: FrozenHeartVariantKey;
  percent: number;
  temperature: string;
  temperatureLabel: string;
  triggerTagD: boolean;
  tagDSupplement?: string;
  answers: Record<number, string>;
}

export function buildFrozenHeartResult(
  answers: Record<number, string>,
  triggerTagD: boolean,
  lang: Lang = 'vi',
  forcedVariant?: string
): FrozenHeartResultPayload {
  const { SINGLE_RESULTS, FROZEN_HEART_VARIANTS, FROZEN_HEART_TAG_D } = getRegistrationData(lang);
  const { type, percent } = scoreSingleQuizAnswers(answers);
  const result = SINGLE_RESULTS[type];
  let variantKey: FrozenHeartVariantKey;
  if (forcedVariant && forcedVariant.length === 2 && forcedVariant[0] === type) {
    variantKey = forcedVariant as FrozenHeartVariantKey;
  } else {
    variantKey = pickFrozenHeartVariant(type);
  }
  const variant = FROZEN_HEART_VARIANTS[variantKey];
  const adviceLabel = lang === 'en' ? 'Matchmaker tip' : 'Lời khuyên từ Matchmaker';
  const desc = `${result.analysis}\n\n💡 ${adviceLabel}: ${result.matchmakerAdvice}`;
  return {
    title: result.title,
    desc: triggerTagD ? `${desc}\n\n⚠️ ${FROZEN_HEART_TAG_D.supplement}` : desc,
    shareTitle: variant.shareTitle,
    shareHook: variant.shareHook,
    typeKey: type,
    variantKey,
    percent,
    temperature: randomFrozenHeartTemperature(type),
    temperatureLabel: result.temperatureLabel,
    triggerTagD,
    tagDSupplement: triggerTagD ? FROZEN_HEART_TAG_D.supplement : undefined,
    answers,
  };
}

/** Bedroom Config Quiz (18+) - 6 câu A/B/C/D, scoring giống Personality */
export function buildBedroomConfigResult(
  answers: Record<number, string>,
  lang: Lang = 'vi',
  forcedVariant?: string
) {
  const { BEDROOM_RESULTS, BEDROOM_VARIANTS } = getRegistrationData(lang);
  const { type, percent } = scoreQuizAnswers(answers);
  const result = BEDROOM_RESULTS[type as keyof typeof BEDROOM_RESULTS];
  let variantKey: string;
  if (forcedVariant && forcedVariant.length === 2 && forcedVariant[0] === type) {
    variantKey = forcedVariant;
  } else {
    variantKey = pickPersonalityVariant(type);
  }
  const variant = BEDROOM_VARIANTS[variantKey];

  return {
    title: result.title,
    desc: result.desc,
    shareTitle: variant.shareTitle,
    shareHook: variant.shareHook,
    typeKey: type,
    variantKey,
    percent,
    answers,
    // Include fixed progress bars for consistent bedroom card visual (same as before)
    progressBars: [
      { icon: '🧸', label: 'Eco Mode' },
      { icon: '🔥', label: 'Balanced' },
      { icon: '🌋', label: 'Adventurous' },
    ],
  };
}